import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const orderNumber = params.get("order_number");
    const [status, setStatus] = useState("polling"); // polling, paid, expired, manual
    const [order, setOrder] = useState(null);
    const [attempts, setAttempts] = useState(0);

    useEffect(() => {
        if (!sessionId && orderNumber) {
            // Manual flow: just show success
            setStatus("manual");
            return;
        }
        if (!sessionId) return;

        let cancelled = false;
        async function poll(n) {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    setOrder(data.order);
                    return;
                }
                if (data.payment_status === "expired" || data.payment_status === "failed") {
                    setStatus("expired");
                    setOrder(data.order);
                    return;
                }
                if (n < 10) {
                    setAttempts(n + 1);
                    setTimeout(() => poll(n + 1), 2000);
                } else {
                    setStatus("timeout");
                    setOrder(data.order);
                }
            } catch (e) {
                if (n < 5) {
                    setTimeout(() => poll(n + 1), 2000);
                } else {
                    setStatus("expired");
                }
            }
        }
        poll(0);
        return () => { cancelled = true; };
    }, [sessionId, orderNumber]);

    return (
        <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
            {status === "polling" && (
                <>
                    <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-6" />
                    <h1 className="font-display text-3xl font-bold mb-3">Verifying your payment…</h1>
                    <p className="text-muted-foreground">Hold tight – this usually takes a few seconds.</p>
                </>
            )}
            {status === "paid" && (
                <>
                    <CheckCircle2 className="w-16 h-16 text-secondary mx-auto mb-6" data-testid="payment-success-icon" />
                    <div className="overline text-secondary mb-2">Payment confirmed</div>
                    <h1 className="font-display text-4xl font-bold mb-4">Thank you for your order!</h1>
                    {order && (
                        <p className="text-muted-foreground mb-6">
                            Order <span className="font-mono font-bold text-ink">{order.order_number}</span> for {money(order.total)} has been received and will be processed shortly. A confirmation has been sent to <strong>{order.shipping_email}</strong>.
                        </p>
                    )}
                    <Link to="/products" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 font-semibold hover:bg-primary-hover">
                        Continue shopping
                    </Link>
                </>
            )}
            {status === "manual" && (
                <>
                    <CheckCircle2 className="w-16 h-16 text-secondary mx-auto mb-6" />
                    <div className="overline text-secondary mb-2">Order received</div>
                    <h1 className="font-display text-4xl font-bold mb-4">Thanks – we're on it!</h1>
                    <p className="text-muted-foreground mb-6">
                        Your order <span className="font-mono font-bold text-ink">{orderNumber}</span> has been recorded. Our team will contact you shortly to confirm payment details and arrange delivery.
                    </p>
                    <Link to="/products" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 font-semibold hover:bg-primary-hover">
                        Continue shopping
                    </Link>
                </>
            )}
            {(status === "expired" || status === "timeout") && (
                <>
                    <XCircle className="w-16 h-16 text-primary mx-auto mb-6" />
                    <h1 className="font-display text-3xl font-bold mb-3">Payment not completed</h1>
                    <p className="text-muted-foreground mb-6">Your payment didn't go through. You can try again from your cart.</p>
                    <Link to="/cart" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 font-semibold hover:bg-primary-hover">
                        Return to cart
                    </Link>
                </>
            )}
        </div>
    );
}
